import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { checkForDuplicates } from "../../api/keyvalue";
import useAuthority from "../../hooks/useAuthority";
import { inputFieldProfile } from "../../const";
import useFieldVisibility from "../../hooks/useFieldVisibility";

const Input = forwardRef(
  (
    {
      value = "",
      onChange = () => {},
      name = "",
      field = "field",
      module = "module",
      form = "form",
      editMode,
      placeholder,
      label,
      onBlur,
      error,
      resetError = () => {},
      type = "text",
      required = false,
      disabled = false,
      readOnly = false,
      duplicate = true,
      caseSensitive = false,
      // Below props are mandatory if duplicate is set to false
      tableName,
      primaryKey,
      searchField,
      companyWise = true,
      activeCol,
      companyCol,
      // Type of the search field ("S" for Comma Seperated values "B" for Others)
      keyType,
      duplicateErrorMessage,
      setErrors,
      errorField,
      id,
      authenticated = false,
      pattern = ".*",
      profile = inputFieldProfile.TEXT,
      style,
      useDynamicFields = false,
      blockPattern = null,
      ...props
    },
    ref,
  ) => {
    const [touched, setTouched] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);

    const {
      visible,
      mandatory,
      label: currentLabel,
    } = useFieldVisibility({
      field,
      useDynamicFields,
      form,
    });

    const [currentValue, setCurrentValue] = useState(value);

    const { view, authorized } = useAuthority({
      module,
      form,
      field,
      editMode,
      authenticated,
    });

    const handleBeforeInput = (e) => {
      const char = e.data;
      const allowedChars = "0123456789.-";

      if (char && !allowedChars.includes(char)) {
        e.preventDefault();
      }
    };

    const sanitizeValue = (val) => {
      if (val == null) return val;
      if (!blockPattern) return val;
      try {
        return String(val).replace(blockPattern, "");
      } catch {
        return val;
      }
    };

    const checkDuplicates = async () => {
      setSearchLoading(true);
      const formatedValue = value ? value.trim() : "";
      if (formatedValue) {
        const response = await checkForDuplicates({
          tableName,
          primaryKeyCol: primaryKey,
          columnName: searchField,
          searchText: formatedValue,
          activeCol: activeCol,
          companyCol: companyWise ? companyCol : "-2",
          isCaseSensitive: caseSensitive,
        });
        if (response) {
          if (id) {
            if (response === id) {
              setErrors((prevState) => {
                const { stkName: _, ...rest } = prevState;
                return rest;
              });
            } else {
              setErrors((prevState) => ({
                ...prevState,
                [errorField]: duplicateErrorMessage,
              }));
            }
          } else {
            setErrors((prevState) => ({
              ...prevState,
              [errorField]: duplicateErrorMessage,
            }));
          }
        } else {
          setErrors((prevState) => {
            const { stkName: _, ...rest } = prevState;
            return rest;
          });
        }
      }
      setSearchLoading(false);
    };

    useEffect(() => {
      // keep in sync if parent updates value
      setCurrentValue(sanitizeValue(value));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useImperativeHandle(ref, () => ({
      value: currentValue,
      reset: () => {
        setCurrentValue("");
      },
    }));

    const handleTextChange = (e) => {
      const raw = e.target.value;
      const next = sanitizeValue(raw);

      // update internal state
      setCurrentValue(next);

      // propagate to parent with cleaned value
      onChange({ ...e, target: { ...e.target, value: next } });
    };

    const handleKeyDown = (e) => {
      // IMPORTANT: do NOT use global regex test here (g flag keeps state)
      // So we create a safe "single char" regex from blockPattern if possible.
      if (blockPattern && e.key && e.key.length === 1) {
        let shouldBlock = false;

        if (blockPattern instanceof RegExp) {
          const flags = blockPattern.flags?.replace("g", "") ?? "";
          const oneCharRe = new RegExp(blockPattern.source, flags);
          shouldBlock = oneCharRe.test(e.key);
        }

        if (shouldBlock) {
          e.preventDefault();
          return;
        }
      }

      props.onKeyDown?.(e);
    };

    const handlePaste = (e) => {
      if (!blockPattern) return;

      const text = e.clipboardData?.getData("text") ?? "";
      const cleaned = sanitizeValue(text);

      if (cleaned !== text) {
        e.preventDefault();

        const el = e.target;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;

        const next = el.value.slice(0, start) + cleaned + el.value.slice(end);

        setCurrentValue(next);
        onChange({ ...e, target: { ...el, value: next } });
      }

      props.onPaste?.(e);
    };

    const defaultField = (
      <>
        <input
          style={
            view
              ? useDynamicFields
                ? visible
                  ? {}
                  : { display: "none" }
                : {}
              : { display: "none" }
          }
          {...props}
          pattern={pattern}
          disabled={disabled || !authorized}
          readOnly={readOnly}
          type={type}
          name={name}
          placeholder={placeholder}
          value={currentValue}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onChange={handleTextChange}
          onClick={() => {
            setTouched(true);
            if (error) {
              resetError();
            }
          }}
          onBlur={() => {
            onBlur && onBlur();
            if (!duplicate) {
              checkDuplicates();
            }
          }}
        />
        <small style={{ color: "#9f3a38" }}>{`${error ? error : ""}`}</small>
      </>
    );

    let inputField;

    switch (profile) {
      case inputFieldProfile.TEXT: {
        inputField = defaultField;
        break;
      }
      case inputFieldProfile.DECIMAL: {
        inputField = (
          <>
            <input
              style={
                view
                  ? useDynamicFields
                    ? visible
                      ? {}
                      : { display: "none" }
                    : {}
                  : { display: "none" }
              }
              {...props}
              pattern="^-?\d*(\.\d*)?$"
              disabled={disabled || !authorized}
              readOnly={readOnly}
              type={type}
              name={name}
              placeholder={placeholder}
              value={currentValue}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onChange={(e) => {
                // keep existing decimal logic intact
                const inputValue = e.target.value.replace(/[^0-9.-]/g, "");
                const regex = /^-?\d*(\.\d*)?$/;
                if (regex.test(inputValue) || inputValue === "") {
                  const next = sanitizeValue(inputValue);
                  setCurrentValue(next);
                  onChange({ ...e, target: { ...e.target, value: next } });
                }
              }}
              onBeforeInput={handleBeforeInput}
              onClick={() => {
                setTouched(true);
                if (error) {
                  resetError();
                }
              }}
              onBlur={() => {
                if (!duplicate) {
                  checkDuplicates();
                }
              }}
            />
            <small
              style={{ color: "#9f3a38" }}
            >{`${error ? error : ""}`}</small>
          </>
        );
        break;
      }
      default: {
        inputField = defaultField;
      }
    }

    if (useDynamicFields && !visible) {
      return;
    }

    return (
      <div style={style ?? {}} className={`field ${error ? "error" : ""}`}>
        <label
          style={
            view
              ? useDynamicFields
                ? visible
                  ? {}
                  : { display: "none" }
                : {}
              : { display: "none" }
          }
        >
          {((useDynamicFields && mandatory) ||
            (!useDynamicFields && required)) && (
            <label
              style={{
                color: "#9f3a38",
                fontSize: "20px",
                marginRight: "2px",
              }}
            >
              *
            </label>
          )}
          {useDynamicFields ? currentLabel : label}
        </label>
        {inputField}
      </div>
    );
  },
);

export default Input;

// import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
// import { checkForDuplicates } from "../../api/keyvalue";
// import useAuthority from "../../hooks/useAuthority";
// import { inputFieldProfile } from "../../const";
// import useFieldVisibility from "../../hooks/useFieldVisibility";

// const Input = forwardRef(
//   (
//     {
//       value = "",
//       onChange = () => {},
//       name = "",
//       field = "field",
//       module = "module",
//       form = "form",
//       editMode,
//       placeholder,
//       label,
//       onBlur,
//       error,
//       resetError = () => {},
//       type = "text",
//       required = false,
//       disabled = false,
//       readOnly = false,
//       duplicate = true,
//       caseSensitive = false,
//       // Below props are mandatory if duplicate is set to false
//       tableName,
//       primaryKey,
//       searchField,
//       companyWise = true,
//       activeCol,
//       companyCol,
//       // Type of the search field ("S" for Comma Seperated values "B" for Others)
//       keyType,
//       duplicateErrorMessage,
//       setErrors,
//       errorField,
//       id,
//       authenticated = false,
//       pattern = ".*",
//       profile = inputFieldProfile.TEXT,
//       style,
//       useDynamicFields = false,
//       ...props
//     },
//     ref,
//   ) => {
//     const [touched, setTouched] = useState(false);
//     const [searchLoading, setSearchLoading] = useState(false);

//     const {
//       visible,
//       mandatory,
//       label: currentLabel,
//     } = useFieldVisibility({ field, useDynamicFields, form });

//     const [currentValue, setCurrentValue] = useState(value);

//     const { view, authorized } = useAuthority({
//       module,
//       form,
//       field,
//       editMode,
//       authenticated,
//     });

//     const handleBeforeInput = (e) => {
//       const char = e.data;
//       const allowedChars = "0123456789.-";

//       if (char && !allowedChars.includes(char)) {
//         e.preventDefault();
//       }
//     };

//     const checkDuplicates = async () => {
//       setSearchLoading(true);
//       const formatedValue = value ? value.trim() : "";
//       if (formatedValue) {
//         const response = await checkForDuplicates({
//           tableName,
//           primaryKeyCol: primaryKey,
//           columnName: searchField,
//           searchText: formatedValue,
//           activeCol: activeCol,
//           companyCol: companyWise ? companyCol : "-2",
//           isCaseSensitive: caseSensitive,
//         });
//         if (response) {
//           if (id) {
//             if (response === id) {
//               setErrors((prevState) => {
//                 const { stkName: _, ...rest } = prevState;
//                 return rest;
//               });
//             } else {
//               setErrors((prevState) => ({
//                 ...prevState,
//                 [errorField]: duplicateErrorMessage,
//               }));
//             }
//           } else {
//             setErrors((prevState) => ({
//               ...prevState,
//               [errorField]: duplicateErrorMessage,
//             }));
//           }
//         } else {
//           setErrors((prevState) => {
//             const { stkName: _, ...rest } = prevState;
//             return rest;
//           });
//         }
//       }
//       setSearchLoading(false);
//     };

//     useEffect(() => {
//       setCurrentValue(value);
//     }, [value]);

//     useImperativeHandle(ref, () => ({
//       value: currentValue,
//       reset: () => {
//         setCurrentValue("");
//       },
//     }));

//     const defaultField = (
//       <>
//         <input
//           style={
//             view
//               ? useDynamicFields
//                 ? visible
//                   ? {}
//                   : { display: "none" }
//                 : {}
//               : { display: "none" }
//           }
//           {...props}
//           pattern={pattern}
//           disabled={disabled || !authorized}
//           readOnly={readOnly}
//           type={type}
//           name={name}
//           placeholder={placeholder}
//           value={currentValue}
//           onChange={(e) => {
//             onChange(e);
//             setCurrentValue(e.target.value);
//           }}
//           onClick={() => {
//             setTouched(true);
//             if (error) {
//               resetError();
//             }
//           }}
//           onBlur={() => {
//             onBlur && onBlur();
//             if (!duplicate) {
//               checkDuplicates();
//             }
//           }}
//         />
//         <small style={{ color: "#9f3a38" }}>{`${error ? error : ""}`}</small>
//       </>
//     );

//     let inputField;

//     switch (profile) {
//       case inputFieldProfile.TEXT: {
//         inputField = defaultField;
//         break;
//       }
//       case inputFieldProfile.DECIMAL: {
//         inputField = (
//           <>
//             <input
//               style={
//                 view
//                   ? useDynamicFields
//                     ? visible
//                       ? {}
//                       : { display: "none" }
//                     : {}
//                   : { display: "none" }
//               }
//               {...props}
//               pattern="^-?\d*(\.\d*)?$"
//               disabled={disabled || !authorized}
//               readOnly={readOnly}
//               type={type}
//               name={name}
//               placeholder={placeholder}
//               value={currentValue}
//               onChange={(e) => {
//                 const inputValue = e.target.value.replace(/[^0-9.-]/g, "");
//                 const regex = /^-?\d*(\.\d*)?$/;
//                 if (regex.test(inputValue) || inputValue === "") {
//                   onChange(e);
//                   setCurrentValue(e.target.value);
//                 }
//               }}
//               onBeforeInput={handleBeforeInput}
//               onClick={() => {
//                 setTouched(true);
//                 if (error) {
//                   resetError();
//                 }
//               }}
//               onBlur={() => {
//                 if (!duplicate) {
//                   checkDuplicates();
//                 }
//               }}
//             />
//             <small
//               style={{ color: "#9f3a38" }}
//             >{`${error ? error : ""}`}</small>
//           </>
//         );
//         break;
//       }
//       default: {
//         inputField = defaultField;
//       }
//     }

//     if (useDynamicFields && !visible) {
//       return;
//     }

//     return (
//       <div style={style ?? {}} className={`field ${error ? "error" : ""}`}>
//         <label
//           style={
//             view
//               ? useDynamicFields
//                 ? visible
//                   ? {}
//                   : { display: "none" }
//                 : {}
//               : { display: "none" }
//           }
//         >
//           {((useDynamicFields && mandatory) ||
//             (!useDynamicFields && required)) && (
//             <label
//               style={{
//                 color: "#9f3a38",
//                 fontSize: "20px",
//                 marginRight: "2px",
//               }}
//             >
//               *
//             </label>
//           )}
//           {useDynamicFields ? currentLabel : label}
//         </label>
//         {inputField}
//       </div>
//     );
//   },
// );

// export default Input;
